import React, { useState, useContext, useEffect } from 'react';
import { getPatientDemographics } from '../../../api/patient_demographics.js';
import { getPatientDemographicsExport } from '../../../api/patient_demographics.js';

import BarChart from '../../ui_kit/charts/bar_chart';
import PieChart from '../../ui_kit/charts/pie_chart';
import StackedBarChart from '../../ui_kit/charts/stacked_bar_chart';
import CompleteDateRangePicker from '../../ui_kit/inputs/complete_date_range_picker';
import IsScrolling from 'react-is-scrolling';
import ExportButton from '../../ui_kit/buttons/export_button';
import Context from '../../../utils/context';
import { isFeatureEnabled, FEATURES } from '../../../helpers/feature_checker';

import styles from './index.module.css';

// To add a new program, add an entry here keyed by the account slug.
// Each entry is an ordered list of charts to show for that program.
// Charts are only rendered if the API returns data for that key (total > 0).
const PROGRAM_CHART_CONFIGS = {
  bylvay: [
    { key: 'patient_age', title: 'Patient Age', type: 'bar' },
    { key: 'caregiver_age', title: 'Caregiver Age', type: 'bar' },
    { key: 'person_in_care_age', title: 'Age of Person In Care', type: 'bar' },
    { key: 'patient_gender', title: 'Patient Gender', type: 'pie' },
    { key: 'caregiver_gender', title: 'Caregiver Gender', type: 'pie' },
    { key: 'patient_or_caregiver', title: 'Patient or Caregiver', type: 'pie' },
    { key: 'condition', title: 'Patient Condition', type: 'pie' },
    { key: 'caring_for', title: 'Caregiver Relationship', type: 'pie' },
    { key: 'has_had_treatment', title: 'Is the Patient Currently Taking BYLVAY?', type: 'pie' },
    {
      key: 'symptom_management',
      title: 'How Long Has the Patient Been Taking BYLVAY?',
      type: 'pie',
      subHeading: '(Current BYLVAY Users Only)',
    },
    { key: 'considering_a_switch', title: 'Considering Starting BYLVAY?', type: 'pie' },
    {
      key: 'has_spoken_with_doctor_about_treatment',
      title: 'Has There Been a Doctor Discussion About BYLVAY?',
      type: 'pie',
    },
    { key: 'scheduled_treatment', title: 'Does the Patient Have a Prescription for BYLVAY?', type: 'pie' },
    {
      key: 'inspired_to_share',
      title: 'Inspired to Share Their Story After Talking with a Mentor',
      type: 'bar',
    },
    {
      key: 'interested_in_mentoring',
      title: 'Interested in Becoming a PatientPartner Mentor',
      type: 'bar',
    },
    {
      key: 'not_ready_reasons',
      title: 'Why Patients May Not Start or Continue BYLVAY',
      type: 'pie',
    },
  ],
  xcopri: [
    { key: 'age', title: 'Number of Patients per Age Group', type: 'bar' },
    { key: 'gender', title: 'User Gender', type: 'pie' },
    { key: 'patient_or_caregiver', title: 'Patient or Caregiver', type: 'pie' },
    { key: 'xcopri_diagnosis', title: 'How Long Have the Patient Been Diagnosed with Epilepsy?', type: 'pie' },
    { key: 'xcopri_last_seisure', title: 'When The Most Recent Seizure Occurred for the Patient', type: 'pie' },
    { key: 'xcopri_seisure_frequency', title: 'How Often is the Patient Having Seizures?', type: 'pie' },
    { key: 'scheduled_treatment', title: 'Is the Patient Currently Taking XCOPRI?', type: 'pie' },
    {
      key: 'symptom_management',
      title: 'How Long the Patient Have Been Using XCOPRI?',
      type: 'pie',
      subHeading: '(Current XCOPRI Users Only)',
    },
    { key: 'xcopri_asm_side_effect', title: 'Side Effects from Current ASM (Excluding XCOPRI)', type: 'bar' },
    { key: 'xcopri_reason_for_switch', title: 'Reasons For Switching', type: 'bar' },
    { key: 'considering_a_switch', title: 'Non-XCOPRI Patients Considering a Switch', type: 'pie' },
    { key: 'connection_preferences', title: 'Preferred Ways to Connect', type: 'bar' },
    { key: 'referral_source', title: 'How They Heard About the Program', type: 'bar', fullWidth: true },
    { key: 'comfort_level', title: 'Did the Mentor Make the Patient Feel More Comfortable?', type: 'pie' },
  ],
  // BEAR groups its charts into labeled sections. An entry with a `section`
  // key renders a heading separator (only when at least one chart in that
  // section has data). The chart order below also drives the CSV export order
  // in PatientDemographicsController, so keep the two in sync.
  bear: [
    { section: 'Program Reach' },
    { key: 'patient_or_caregiver', title: 'Patient or Caregiver', type: 'pie' },
    { key: 'patient_age', title: 'Patient Age', type: 'bar' },
    { key: 'caregiver_age', title: 'Caregiver Age', type: 'bar' },
    { key: 'person_in_care_age', title: 'Age of Person In Care', type: 'bar' },
    { key: 'patient_gender', title: 'Patient Gender', type: 'pie' },
    { key: 'caregiver_gender', title: 'Caregiver Gender', type: 'pie' },
    { key: 'relationship_to_patient', title: 'Caregiver Relationship', type: 'pie' },
    { key: 'referral_source', title: 'How They Heard About the Program', type: 'bar', fullWidth: true },

    { section: 'Profile at Intake' },
    { key: 'activity_level', title: 'Activity Level Before Injury', type: 'bar', fullWidth: true },
    { key: 'first_acl_injury', title: 'First ACL Injury?', type: 'bar', fullWidth: true },
    { key: 'has_had_treatment', title: 'ACL Surgery Status', type: 'pie' },
    { key: 'bear_implant_used', title: 'BEAR® Implant Used for Surgery?', type: 'pie' },
    {
      key: 'has_spoken_with_doctor_about_treatment',
      title: 'Spoken With a Doctor About the BEAR® Implant?',
      type: 'pie',
    },
    { key: 'confidence_level', title: 'Pre-Conversation Confidence Level', type: 'bar' },
    { key: 'biggest_concerns', title: 'Biggest Concerns', type: 'bar', fullWidth: true },
    { key: 'likelihood_to_proceed', title: 'Pre-Conversation Likelihood to Proceed', type: 'bar', fullWidth: true },
    {
      key: 'treatment_leaning',
      title: 'Where the Patient Is Leaning (Pre-Conversation)',
      type: 'bar',
      fullWidth: true,
    },
    { key: 'looking_for_information', title: 'Interested in Finding a Doctor?', type: 'pie' },

    { section: 'Program Impact' },
    { key: 'confidence_change', title: 'How the Conversation Changed Confidence', type: 'bar', fullWidth: true },
    {
      key: 'confidence_comparison',
      title: 'Confidence Level: Before vs. After Mentor Conversation',
      type: 'stacked_bar',
      fullWidth: true,
    },
    {
      key: 'likelihood_comparison',
      title: 'Likelihood to Proceed: Before vs. After Mentor Conversation',
      type: 'stacked_bar',
      fullWidth: true,
    },
    {
      key: 'leaning_comparison',
      title: 'Treatment Leaning: Before vs. After Mentor Conversation',
      type: 'stacked_bar',
      fullWidth: true,
    },

    { section: 'Outcomes and Next Steps' },
    { key: 'ready_for_next_step', title: 'Ready for Next Step', type: 'pie' },
    { key: 'find_doctor', title: 'Would Like Help Finding a Doctor', type: 'pie' },
    { key: 'why_not_ready', title: 'Why They May Not Move Forward', type: 'bar', fullWidth: true },

    { section: 'Experience and Advocacy' },
    { key: 'recommend_program', title: 'Would Recommend the Program', type: 'bar' },
    { key: 'connection', title: 'Feels More Connected to Others With a Similar Experience', type: 'pie' },
    { key: 'become_mentor', title: 'Would Consider Becoming a Mentor', type: 'pie' },
  ],
  anktiva: [
    { section: 'Program Reach' },
    { key: 'age', title: 'Patient Age', type: 'bar' },
    { key: 'gender', title: 'Patient Gender', type: 'pie' },

    { section: 'Profile at Intake' },
    { key: 'nmibc_cis_diagnosis', title: 'NMIBC CIS Diagnosis?', type: 'pie' },
    { key: 'bcg_unresponsive', title: 'BCG-Unresponsive?', type: 'pie' },
    { key: 'has_had_treatment', title: 'Has Received Anktiva?', type: 'pie' },
    {
      key: 'treatment_duration',
      title: 'Length of time receiving Anktiva',
      type: 'pie',
    },
    { key: 'considering_treatment', title: 'Considering Anktiva?', type: 'pie' },
    {
      key: 'has_spoken_with_doctor_about_treatment',
      title: 'Spoken with Doctor about Anktiva?',
      type: 'pie',
    },
    { key: 'scheduled_treatment', title: 'Doctor Recommended or Scheduled Anktiva?', type: 'pie' },
    {
      key: 'treatment_decision_stage',
      title: 'Treatment Decision Stage at Intake',
      type: 'bar',
      fullWidth: true,
    },
    { key: 'treatment_confidence', title: 'Confidence in Treatment Decision at Intake', type: 'bar' },
    { key: 'treatment_likelihood', title: 'Likelihood to Start or Continue Anktiva at Intake', type: 'bar' },

    { section: 'Program Impact' },
    {
      key: 'treatment_decision_comparison',
      title: 'Treatment Decision Stage: Before vs. After Mentor Conversation',
      type: 'stacked_bar',
      fullWidth: true,
    },
    {
      key: 'treatment_confidence_comparison',
      title: 'Confidence in Treatment Decision: Before vs. After Mentor Conversation',
      type: 'stacked_bar',
      fullWidth: true,
    },
    {
      key: 'treatment_likelihood_comparison',
      title: 'Likelihood to Start or Continue Anktiva: Before vs. After Mentor Conversation',
      type: 'stacked_bar',
      fullWidth: true,
    },

    { section: 'Outcomes and Next Steps' },
    { key: 'ready_for_next_step', title: 'Plans to Start or Continue Anktiva', type: 'pie' },
    {
      key: 'not_ready_reasons',
      title: 'Why Patients May Not Start or Continue Anktiva',
      type: 'bar',
      fullWidth: true,
    },
    { key: 'confidence_change', title: 'How the Conversation Changed Confidence', type: 'bar', fullWidth: true },

    { section: 'Experience and Advocacy' },
    { key: 'helpful_and_supportive', title: 'Conversation Was Helpful and Supportive', type: 'bar' },
    { key: 'connection_with_others', title: 'Feels More Connected to Others', type: 'pie' },
    { key: 'recommend_program', title: 'Would Recommend the Program', type: 'bar' },
    { key: 'interested_in_mentoring', title: 'Interested in Becoming a PatientPartner Mentor', type: 'bar' },
  ],
};

function PatientDemographics({ isScrolling }) {
  const [chartData, setChartData] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const context = useContext(Context);
  const currentUser = context.auth.getCurrentUser();
  const currentAccount = currentUser.account;
  const demographicsEnabled = isFeatureEnabled(currentAccount, FEATURES.PATIENT_DEMOGRAPHICS);
  const accountSlug = currentAccount?.slug;

  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
    } else {
      if (!startDate || !endDate) return;
    }

    const params = {};
    if (startDate !== null) params.start_date = startDate.toISOString().split('T')[0];
    if (endDate !== null) params.end_date = endDate.toISOString().split('T')[0];

    loadData(params);
  }, [startDate, endDate]);

  const loadData = async (params) => {
    try {
      await new Promise((resolve, reject) => {
        getPatientDemographics(params, (data) => {
          try {
            setChartData(data);
            resolve();
          } catch (error) {
            reject(error);
          }
        });
      });
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const hasData = (key) => {
    const entry = chartData[key];
    return entry && entry.total > 0;
  };

  const renderChart = (config) => {
    if (!hasData(config.key)) return null;

    const data = chartData[config.key];
    let ChartComponent;
    if (config.type === 'bar') {
      ChartComponent = BarChart;
    } else if (config.type === 'stacked_bar') {
      ChartComponent = StackedBarChart;
    } else {
      ChartComponent = PieChart;
    }

    const props = { title: config.title, data };

    if (config.type === 'bar' || config.type === 'stacked_bar') {
      props.yTitle = '';
      props.xTitle = '';
    }

    if (config.subHeading) {
      props.subHeading = config.subHeading;
    }

    const columnClass = config.fullWidth ? 'col-12' : 'col-12 col-md-6';

    return (
      <div key={config.key} className={columnClass}>
        <div className={styles.card}>
          <ChartComponent {...props} />
        </div>
      </div>
    );
  };

  // Full-width charts always sit on their own row so they can use the entire
  // container width. Half-width charts pair up two-per-row in declared order.
  // A `{ section }` entry renders a heading separator, but only once a chart in
  // that section actually has data, so empty sections don't leave orphan headers.
  const renderChartRows = (configs) => {
    const rows = [];
    let halfRowBuffer = [];
    let pendingSection = null;

    const flushHalfRow = () => {
      if (halfRowBuffer.length === 0) return;
      rows.push(
        <div key={`row-${rows.length}`} className='row'>
          {halfRowBuffer}
        </div>
      );
      halfRowBuffer = [];
    };

    const flushSectionHeading = () => {
      if (!pendingSection) return;
      flushHalfRow();
      rows.push(
        <div key={`section-${rows.length}`} className='row'>
          <div className='col-12'>
            <h2 className={styles.section_header}>{pendingSection}</h2>
          </div>
        </div>
      );
      pendingSection = null;
    };

    configs.forEach((config) => {
      if (config.section) {
        pendingSection = config.section;
        return;
      }

      const rendered = renderChart(config);
      if (!rendered) return;

      flushSectionHeading();

      if (config.fullWidth) {
        flushHalfRow();
        rows.push(
          <div key={`row-${rows.length}`} className='row'>
            {rendered}
          </div>
        );
      } else {
        halfRowBuffer.push(rendered);
        if (halfRowBuffer.length === 2) flushHalfRow();
      }
    });

    flushHalfRow();
    return rows;
  };

  const programChartConfigs = PROGRAM_CHART_CONFIGS[accountSlug] || [];

  return (
    <div className='container marg-bottom-50'>
      <div className='row'>
        <h1 className={`${styles.header} col-lg-6`} style={{ paddingLeft: '25px' }}>
          Mentee Demographics
        </h1>

        <CompleteDateRangePicker
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          startDate={startDate}
          endDate={endDate}
          isScrolling={isScrolling}
          style={{ marginLeft: 'auto' }}
        />
      </div>
      <div className='row' style={{ marginBottom: '0px' }}>
        <ExportButton
          requestExport={getPatientDemographicsExport}
          params={{
            ...(startDate && { start_date: startDate.toISOString().split('T')[0] }),
            ...(endDate && { end_date: endDate.toISOString().split('T')[0] }),
          }}
        />
      </div>

      {demographicsEnabled && renderChartRows(programChartConfigs)}
    </div>
  );
}

export default IsScrolling(PatientDemographics);
